<!-- student-dashboard.wrapper.component.html -->
<!-- Custom dialog title with close button that passes dataChanged flag -->
<div mat-dialog-title>
    <h1>Student Dashboard, {{ (studentDashboardObservable | async)?.['#studentName'] }}</h1>
    <button mat-button (click)="closeDialogWithData()">
        <mat-icon aria-hidden="false" aria-label="Close">close</mat-icon>
    </button>
</div>

<div [ngClass]="{'loading-shade': showProgressSpinner}">
  <ng-container *ngIf="studentDashboardObservable | async as data">
    <div *ngIf="!showProgressSpinner">

		<div mat-dialog-content style="min-height:400px;">

			<!-- Student Summary  -->
			<div *ngIf="data" id="student-summary" class="row justify-content-center">
				<h3 class="text-center mb-3"><b>{{ data['#studentName'] }}</b> Summary</h3>

				<!-- Changed col-2 to col-6 col-md-4 col-lg-2 for better mobile layout -->
				<div class="col-6 col-md-4 col-lg-2 text-center mb-3">
					<div class="summary-circle" style="border-color: #68ff94;">
						<span class="summary-number">{{ data['#totalLessonsEnrolled'] | number:'1.0-1' }}</span>
					</div>
					<div class="summary-label">Total Lessons Enrolled</div>
				</div>

				<div class="col-6 col-md-4 col-lg-2 text-center mb-3">
					<div class="summary-circle" style="border-color: #68ff94;">
						<span class="summary-number">{{ data['#totalLessonsTaken'] | number:'1.0-1' }}</span>
					</div>
					<div class="summary-label">Total Lessons Taken</div>
				</div>

				<div class="col-6 col-md-4 col-lg-2 text-center mb-3">
					<div class="summary-circle" style="border-color: #FCC761;">
						<span class="summary-number">{{ data['#totalLessonsRemaining'] | number:'1.0-1' }}</span>
					</div>
					<div class="summary-label">Total Lessons Remaining</div>
				</div>

				<div class="col-6 col-md-4 col-lg-2 text-center mb-3">
					<div class="summary-circle" [style.border-color]="data['#totalLessonsPaidAhead'] < 0 ? 'red' : '#E5E5E1'">
						<span class="summary-number" [style.color]="data['#totalLessonsPaidAhead'] < 0 ? 'red' : ''">{{ (data['#totalLessonsPaidAhead'] || 0) | number:'1.0-1' }}</span>
					</div>
					<div class="summary-label">Total Lessons Paid Ahead</div>
				</div>

				<div class="col-6 col-md-4 col-lg-2 text-center mb-3">
					<div class="summary-circle" style="border-color: #0077B7;">
						<span class="summary-number">{{ data['#totalFutureLessons'] | number:'1.0-1' }}</span>
					</div>
					<div class="summary-label">Total Future Lessons</div>
				</div>
			</div>

			<!-- Balances come from later requests than the one that clears the
			     spinner; gate on the balance payloads so a real balance is never
			     painted as $0.00 while they are still in flight. -->
			<div *ngIf="data && enrollmentBalance && overallEnrollmentBalance" class="row text-center mt-4">
				<div class="col-lg-3" style="white-space: nowrap;">
					<strong>Enrollment Total:</strong> {{ enrollmentBalance?.unused_lesson_value | studioCurrency }}
					<strong>Available: </strong> {{ enrollmentBalance?.remaining_amount | studioCurrency }}
				</div>
				<div class="col-lg-6">
					<strong>Overall Balance Due: </strong>
					<span>{{ overallBalanceDueWithTax | studioCurrency }}</span>

					<strong id="paymentDue" #paymentDueLabel>/ Payment Due: </strong>
					<ng-container *ngIf="overallEnrollmentBalance?.next_payment_due_date; else noNextPayment">
						<span>{{ nextPaymentDueWithTax | studioCurrency }} ({{ formatDashboardDate(overallEnrollmentBalance?.next_payment_due_date) }})</span>
					</ng-container>
					<ng-template #noNextPayment>
						<span>No Next Payment</span>
					</ng-template>

				</div>
				<div class="col-lg-3">
					<strong>Next Lesson Scheduled: </strong>
					<span>{{ formatNextLesson(nextLessonScheduled) }}</span>
				</div>
			</div>

			<div class="">
				<div class="row">
					<div class="col-lg-5 justify-content-center">
						<!-- Student Basic/Inquiry Info -->
						<mat-card *ngIf="data" id="student-basic-info" class="student-info-card">
							<mat-card-title>
								Student Information
							</mat-card-title>
							<mat-card-content>
								<!-- Student and Partner Information Display -->
								<div class="student-info-container">
									<div class="row g-3">
										<!-- Student Information -->
										<div class="col-12 col-sm-6">
											<div class="info-section">
												<p class="mb-2"><strong>{{data?.['#studentBasicInfoWithPartner']?.['student']?.['name']}} {{data?.['#studentBasicInfoWithPartner']?.['student']?.['lastName']}}</strong></p>
												<p class="mb-2">
													{{ data?.['#studentBasicInfoWithPartner']?.['student']?.['cell'] ? (data?.['#studentBasicInfoWithPartner']?.['student']?.['cell'] | phone) : 'Cell: N/A' }}<br/>
													{{ data?.['#studentBasicInfoWithPartner']?.['student']?.['email'] || 'Email: N/A' }}
													<ng-container *ngIf="data?.['#studentBasicInfoWithPartner']?.['student']?.['birthday']"><br/>Birthday: {{ formatDashboardDate(data?.['#studentBasicInfoWithPartner']?.['student']?.['birthday']) }}</ng-container>
												</p>
												<div class="address-section">
													{{ data?.['#studentBasicInfoWithPartner']?.['student']?.['address']?.['address_line1'] || 'Address: N/A' }}<br/>
													{{ data?.['#studentBasicInfoWithPartner']?.['student']?.['address']?.['locality'] || '' }}
													<span *ngIf="data?.['#studentBasicInfoWithPartner']?.['student']?.['address']?.['locality'] && data?.['#studentBasicInfoWithPartner']?.['student']?.['address']?.['administrative_area']">,</span>
													{{ data?.['#studentBasicInfoWithPartner']?.['student']?.['address']?.['administrative_area'] || '' }}
													{{ data?.['#studentBasicInfoWithPartner']?.['student']?.['address']?.['postal_code'] || '' }}
												</div>
											</div>
										</div>

										<!-- Partner Information -->
										<div class="col-12 col-sm-6" *ngIf="data?.['#studentBasicInfoWithPartner']?.['partner']?.['name']">
											<div class="info-section partner-section">
												<p class="mb-2"><strong>{{data?.['#studentBasicInfoWithPartner']?.['partner']?.['name']}} {{data?.['#studentBasicInfoWithPartner']?.['partner']?.['lastName']}}</strong></p>
												<p class="mb-2">
													{{ data?.['#studentBasicInfoWithPartner']?.['partner']?.['cell'] ? (data?.['#studentBasicInfoWithPartner']?.['partner']?.['cell'] | phone) : 'Cell: N/A' }}<br/>
													{{ data?.['#studentBasicInfoWithPartner']?.['partner']?.['email'] || 'Email: N/A' }}
													<ng-container *ngIf="data?.['#studentBasicInfoWithPartner']?.['partner']?.['birthday']"><br/>Birthday: {{ formatDashboardDate(data?.['#studentBasicInfoWithPartner']?.['partner']?.['birthday']) }}</ng-container>
												</p>
												<div class="address-section">
													{{ data?.['#studentBasicInfoWithPartner']?.['partner']?.['address']?.['address_line1'] || 'Address: N/A' }}<br/>
													{{ data?.['#studentBasicInfoWithPartner']?.['partner']?.['address']?.['locality'] || '' }}
													<span *ngIf="data?.['#studentBasicInfoWithPartner']?.['partner']?.['address']?.['locality'] && data?.['#studentBasicInfoWithPartner']?.['partner']?.['address']?.['administrative_area']">,</span>
													{{ data?.['#studentBasicInfoWithPartner']?.['partner']?.['address']?.['administrative_area'] || '' }}
													{{ data?.['#studentBasicInfoWithPartner']?.['partner']?.['address']?.['postal_code'] || '' }}
												</div>
											</div>
										</div>
									</div>
								</div>
							</mat-card-content>

							<mat-card-actions>
								<div class="row g-2" *ngIf="_permissionsService.canAccess('students_edit')">
									<div class="col-6">
										<button
											*ngIf="data?.['#studentBasicInfoWithPartner']?.['student']?.['contact_id']"
											class="w-100 addLesson"
											(click)="openEntityComponent(StudentRecordEntityComponent, 'contacts', 'student_record', 'edit', data?.['#studentBasicInfoWithPartner']?.['student']?.['contact_id'])"
											mat-flat-button>
											Edit User
										</button>
									</div>
									<div class="col-6">
										<button
											*ngIf="data?.['#studentBasicInfoWithPartner']?.['partner']?.['contact_id']"
											class="w-100 addLesson"
											(click)="openEntityComponent(StudentRecordEntityComponent, 'contacts', 'student_record', 'edit', data?.['#studentBasicInfoWithPartner']?.['partner']?.['contact_id'])"
											mat-flat-button>
											Edit Partner
										</button>
									</div>
								</div>
							</mat-card-actions>
						</mat-card>
					</div>

					<div class="col-lg-2 image-container">
						<div style="width:203.66px; position:relative;">
							<div *ngIf="studentBadgeInfo?.['status']"
								 [class.badge-shield-clickable]="_permissionsService.canAccess('students_edit')"
								 (click)="_permissionsService.canAccess('students_edit') && openDepartmentDialog()"
								 [matTooltip]="_permissionsService.canAccess('students_edit') ? 'Click to change department' : ''">
							<img *ngIf="studentBadgeInfo?.['status'] === 'Active'; else inactiveImage"
									class="img-fluid"
									src="assets/images/ActiveShield-Blank2.jpg"
									alt="Active Shield" />
							<ng-template #inactiveImage>
								<img class="img-fluid"
										src="assets/images/InactiveShield-Blank.jpg"
										alt="Inactive Shield" />
							</ng-template>
							<div class="overlay-text top-overlay">{{studentBadgeInfo?.['department']}}</div>
							<div class="overlay-text bottom-overlay">{{studentBadgeInfo?.['status']}}</div>
							</div>
						</div>
					</div>

					<div class="col-lg-5 justify-content-center">
						<!-- Student Basic/Inquiry Info -->
						<mat-card style="height:auto; min-height:260px;" *ngIf="data">
							<mat-card-title>
								Inquiry Details
							</mat-card-title>
							<mat-card-content>
								<!-- <div class="table-responsive"> -->
									<table class="table inquiry-table" *ngIf="data">
										<tr>
											<th class="text-nowrap">Inquired</th>
											<th class="text-nowrap">Booked</th>
											<th class="text-nowrap">Showed</th>
											<th class="text-nowrap">Inq. Method</th>
											<th class="text-nowrap">Inq. Taker</th>
										</tr>
										<tr>
											<td class="text-nowrap">
												{{ (data?.['#inquiry']?.['#rows']?.['0']?.['inquired'] == '') ? 'Not inquired.' : '' }}
												{{ (data?.['#inquiry']?.['#rows']?.['0']?.['inquired'] != '' ? reportDateService.formatDate(data?.['#inquiry']?.['#rows']?.['0']?.['inquired']) : '') }}
											</td>
											<td class="text-nowrap">
													{{ (data?.['#inquiry']?.['#rows']?.['0']?.['bookedOn'] == '') ? 'Not yet booked.' : '' }}
													{{ (data?.['#inquiry']?.['#rows']?.['0']?.['bookedOn'] != '' ? reportDateService.formatDate(data?.['#inquiry']?.['#rows']?.['0']?.['bookedOn']) : '') }}
											</td>
											<td class="text-nowrap">
													{{ (data?.['#inquiry']?.['#rows']?.['0']?.['showed'] == '') ? 'Not showed yet.' : '' }}
													{{ (data?.['#inquiry']?.['#rows']?.['0']?.['showed'] != '' ? reportDateService.formatDate(data?.['#inquiry']?.['#rows']?.['0']?.['showed']) : '') }}
											</td>
											<td class="text-nowrap">
												{{ data?.['#inquiry']?.['#rows']?.['0']?.['inquiryMethod'] }}
											</td>
											<td class="text-nowrap">
												{{ data?.['#inquiry']?.['#rows']?.['0']?.['inquiryTaker'] }}
											</td>
										</tr>
										<tr *ngIf="data?.['#inquiry']?.['#rows']?.['0']?.['danceTypes']?.length">
											<th colspan="5">Dance Interests</th>
										</tr>
										<tr *ngIf="data?.['#inquiry']?.['#rows']?.['0']?.['danceTypes']?.length">
											<td colspan="5">
												<div class="dance-types-list">
													<span class="dance-type-badge" *ngFor="let type of data?.['#inquiry']?.['#rows']?.['0']?.['danceTypes']">
														{{ getDanceTypeLabel(type) }}
													</span>
												</div>
											</td>
										</tr>
										<tr>
											<th colspan="5">Notes</th>
										</tr>
										<tr>
											<td colspan="5">
												<div class="inquiry-notes">
													{{ data?.['#inquiry']?.['#rows']?.['0']?.['notes'] | truncate:500:true }}
												</div>
											</td>
										</tr>
									</table>
								<!-- </div> -->
								<!-- Needs a loaded account: with none, the inquiry dialog would edit
								     and attach contacts to student_account/undefined. -->
								<button *ngIf="_permissionsService.canAccess('students_edit') && studentAccountID"
									class="me-1 addLesson w-100 mt-3"
									mat-flat-button
									(click)="openEntityComponent(StudentInquiryDetailsComponent, 'student_accounts', 'student_account', 'edit', studentAccountID)">
									Edit Inquiry Information
								</button>
							</mat-card-content>
						</mat-card>

					</div>
				</div>

			</div>

			<div class="">
				<mat-card>
					<mat-card-title>
						Upcoming Lessons
					</mat-card-title>
					<app-student-future-lessons [drupal_student_id]="this.user.id" [drupal_student_name]="data['#studentName']"></app-student-future-lessons>
				</mat-card>
			</div>

			<div *ngIf="data" class="mt-4" style="min-height:600px;">
				<mat-card>
				<div class="row">
					<!-- Tab labels (horizontal, above content) -->
					<div class="col-12">
						<!-- Primary sections -->
						<nav mat-tab-nav-bar class="dashboard-tab-nav">
							<a mat-tab-link [active]="selectedTab === 0" (click)="selectedTab = 0">Lesson List</a>
							<a mat-tab-link [active]="isEnrollmentSection" (click)="selectedTab = 1">Enrollments</a>
							<a mat-tab-link [active]="selectedTab === 9" (click)="selectedTab = 9">Lesson Blocks</a>
							<a mat-tab-link [active]="selectedTab === 2" (click)="selectedTab = 2">Payments</a>
							<a mat-tab-link [active]="selectedTab === 7" (click)="selectedTab = 7">Service List</a>
							<a mat-tab-link [active]="selectedTab === 8" (click)="selectedTab = 8">View Legacy Data</a>
						</nav>

						<!-- Enrollment type filter (contextual) -->
						<div *ngIf="isEnrollmentSection" class="d-flex align-items-center flex-wrap gap-2 mt-3">
							<span class="text-uppercase text-muted small fw-bold me-1">Type</span>
							<div class="dance-types-list">
								<span class="dance-type-badge cursor-pointer" [class.active]="selectedTab === 1" (click)="selectedTab = 1">Lesson Enrollments</span>
								<span class="dance-type-badge cursor-pointer" [class.active]="selectedTab === 3" (click)="selectedTab = 3">Miscellaneous</span>
								<span class="dance-type-badge cursor-pointer" [class.active]="selectedTab === 4" (click)="selectedTab = 4">Sundry</span>
								<span class="dance-type-badge cursor-pointer" [class.active]="selectedTab === 5" (click)="selectedTab = 5">Non Unit Lessons</span>
								<span class="dance-type-badge cursor-pointer" [class.active]="selectedTab === 6" (click)="selectedTab = 6">Non Unit Enrollments</span>
							</div>
						</div>
					</div>

					<!-- Tab content -->
					<div class="col-12 mt-1">
						<div *ngIf="selectedTab === 0">
								<div class="tab-toolbar">
									<div class="tab-toolbar__title">Lesson history</div>
									<div class="tab-toolbar__actions">
										<button *ngIf="_permissionsService.canAccess('lessons_create')" class="addLesson" mat-flat-button
											(click)="openLessonEditComponent(EventLessonEntityComponent, 'events', 'lessons', 'create', null, {
												'__field_students_inline_form': {
													'type': 'attendees',
													'bundle': 'attendance',
													'field_student_account': data['#studentName'] + ' (' + studentAccountID + ')'
												}
											})" mat-button>
											Add Lesson
										</button>
									</div>
								</div>


							<div class="row">
								<div class="col-12">
									<div class="table-responsive">
										<div class="overflow-auto">
												<table class="mb-3" mat-table matSort [dataSource]="lessonList2Rows">

													<!-- Date  -->
													<ng-container matColumnDef="date">
														<th mat-header-cell *matHeaderCellDef mat-sort-header>Date</th>
											<td mat-cell *matCellDef="let element">
															{{ formatDashboardDateTime(element.date_and_time) }}
															<span class="period" [ngClass]="{'am-time-badge': getDashboardTimePeriod(element.date_and_time) === 'AM', 'pm-time-badge': getDashboardTimePeriod(element.date_and_time) === 'PM'}">
																{{ getDashboardTimePeriod(element.date_and_time) }}
															</span>
														</td>
													</ng-container>

													<!-- Service  -->
													<ng-container matColumnDef="service">
														<th mat-header-cell *matHeaderCellDef>Service</th>
														<td mat-cell *matCellDef="let element">
															{{ element.service }}
														</td>
													</ng-container>

													<!-- Length/Duration  -->
													<ng-container matColumnDef="length">
														<th mat-header-cell *matHeaderCellDef>Length</th>
														<td mat-cell *matCellDef="let element">
															{{ element.duration }}
														</td>
													</ng-container>

													<!-- Teacher  -->
													<ng-container matColumnDef="teacher">
														<th mat-header-cell *matHeaderCellDef>Teacher</th>
														<td mat-cell *matCellDef="let element">
															{{ element.teacher }}
														</td>
													</ng-container>

													<!-- Contacts Present  -->
													<ng-container matColumnDef="contacts_present">
														<th mat-header-cell *matHeaderCellDef>Contacts Present</th>
														<td mat-cell *matCellDef="let element">
															{{element.contacts_present}}
														</td>
													</ng-container>

													<!-- Status  -->
													<ng-container matColumnDef="status">
														<th mat-header-cell *matHeaderCellDef>Status</th>
														<td mat-cell *matCellDef="let element">
															<div *ngIf="_permissionsService.canAccess('lessons_edit')">
																<div *ngIf="element.bundle !== 'enrollment'" class="dropdown">
																	<button class="btn btn-sm dropdown-toggle status-badge" [ngClass]="getStatusClass(element.status)" type="button" id="statusDropdown{{element.id}}" data-bs-toggle="dropdown" aria-expanded="false">
																		<span *ngIf="!element.isUpdating">{{ getStatusText(element.status) }}</span>
																		<span *ngIf="element.isUpdating" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
																	</button>
																	<ul class="dropdown-menu" aria-labelledby="statusDropdown{{element.id}}">
																		<li *ngFor="let status of lessonStatuses">
																			<a class="dropdown-item cursor-pointer" (click)="updateStatus(element.id, status.id)">{{ status.name }}</a>
																		</li>
																	</ul>
																</div>
																<!-- <span *ngIf="element.bundle !== 'enrollment' && !element.isUpdating" class="amt-bg-red badge">{{ element.status === '' ? 'Pending Status' : '' }}</span> -->
															</div>
															<div *ngIf="!_permissionsService.canAccess('lessons_edit')">
																<span class="amt-bg-gray badge">{{ element.status == '63' ? 'Cancelled' : '' }}</span>
																<span class="amt-bg-gray badge">{{ element.status == '61' ? 'No Showed Not Charged' : '' }}</span>
																<span class="amt-bg-canceled-too-late badge">{{ element.status == '60' ? 'No Showed, Charged' : '' }}</span>
																<span class="amt-bg-canceled-too-late badge">{{ element.status == '1425' ? 'Canceled Too Late' : '' }}</span>
																<span class="amt-bg-red badge">{{ element.status == '64' ? 'Pending Status' : '' }}</span>
																<span class="amt-bg-red badge">{{ element.status == '' ? '' : '' }}</span>
																<span class="amt-bg-purple badge">{{ element.status == '62' ? 'Rescheduled' : '' }}</span>
																<span class="amt-bg-green badge">{{ element.status == '59' ? 'Showed' : '' }}</span>
																<span class="amt-bg-red badge">{{ element.status == '' && element.bundle != 'enrollment' ? 'Pending Status' : '' }}</span>
															</div>
														</td>
													</ng-container>

													<!-- Payment Status  -->
													<ng-container matColumnDef="payment_status">
														<th mat-header-cell *matHeaderCellDef>Payment</th>
														<td mat-cell *matCellDef="let element">
															<span *ngIf="element.payment_status" class="payment-status">
																<span class="payment-dot" [ngClass]="{
																	'payment-paid': element.payment_status === 'paid',
																	'payment-unpaid': element.payment_status === 'unpaid',
																	'payment-partial': element.payment_status === 'partially_paid',
																	'payment-ahead': element.payment_status === 'paid_ahead'
																}"></span>
																{{ getPaymentStatusLabel(element.payment_status) }}
															</span>
														</td>
													</ng-container>

													<!-- Taken / Rem  -->
													<ng-container matColumnDef="taken / rem">
														<th mat-header-cell *matHeaderCellDef>Taken / Rem</th>
														<td mat-cell *matCellDef="let element">
															<ng-container *ngIf="element.bundle !== 'group_lesson'; else noLessonCounts">
																{{ element.taken }}<span *ngIf="element.taken !== '' && element.remaining !== ''"> / </span><span [style.color]="element.remaining < 0 ? 'red' : ''">{{ element.remaining }}</span>
															</ng-container>
															<ng-template #noLessonCounts></ng-template>
														</td>
													</ng-container>

													<!-- Edit  -->
													<ng-container matColumnDef="edit">
														<th mat-header-cell *matHeaderCellDef>Edit</th>
														<td mat-cell *matCellDef="let element">
															<mat-icon
																class="cursor-pointer"
																*ngIf="element.bundle == 'lesson' && _permissionsService.canAccess('lessons_edit')"
																(click)="openLessonEditComponent(EventLessonEntityComponent, 'events', 'lesson', 'edit', element.id)">
																edit_square
															</mat-icon>
														</td>
													</ng-container>

													<!-- Row shown when there is no matching data. -->
													<tr class="mat-row" *matNoDataRow>
														<td class="mat-cell" colspan="4">No lessons taken.</td>
													</tr>

													<tr mat-header-row *matHeaderRowDef="lessonListColumns"></tr>
													<tr [ngClass]="{'bg-success': element.bundle === 'enrollment'}" mat-row *matRowDef="let element; columns: lessonListColumns;"></tr>

												</table>
										</div>
								</div>
							</div>
						</div>

							<!-- <mat-paginator [pageSizeOptions]="[10, 25, 100]" aria-label="Select page of users"></mat-paginator> -->
						</div>

						<div *ngIf="selectedTab === 9">
							<!-- Content for Lesson Blocks -->
							<div class="row">
								<div class="col-12">
									<div class="tab-toolbar">
										<div class="tab-toolbar__title">Lesson blocks</div>
										<div class="tab-toolbar__actions">
											<button
												mat-flat-button
												color="primary"
												(click)="toggleLessonBlockFilter()">
												<mat-icon>filter_alt</mat-icon>
												{{ lessonBlockStartFromComment ? 'Show All Lessons' : 'Show From Latest Comment' }}
											</button>
											<button style="background-color: transparent !important; border: none;"
												*ngIf="lessonBlockData && lessonBlockData.data && lessonBlockData.data.length > 0"
												(click)="printLessonBlocks()"
												type="button"
												mat-icon-button
												class="mb-1"
												matTooltip="Print lesson blocks"
												matTooltipPosition="below"
												aria-label="Print lesson blocks">
												<mat-icon style="color: #1C3F8B;">print</mat-icon>
											</button>
										</div>
									</div>

									<!-- Filter Input -->
									<div class="row mb-3 d-none" *ngIf="lessonBlockData && lessonBlockData.data && lessonBlockData.data.length > 0">
										<div class="col-md-6 offset-md-3">
											<mat-form-field class="w-100" appearance="outline">
												<mat-label>Filter lesson blocks</mat-label>
												<input matInput (keyup)="applyLessonBlockFilter($event)" placeholder="Search by date, teacher, material, comments, or rating...">
												<mat-icon matSuffix>search</mat-icon>
											</mat-form-field>
										</div>
									</div>

									<div *ngIf="lessonBlockData && lessonBlockData.data && lessonBlockData.data.length > 0; else noLessonBlocks" class="table-responsive">
										<table mat-table [dataSource]="lessonBlockData" class="w-100">
											<!-- Date Column -->
											<ng-container matColumnDef="date">
												<th mat-header-cell *matHeaderCellDef>Date</th>
												<td mat-cell *matCellDef="let element">
													{{ formatDashboardDateTime(element.date_and_time) }}
													<span class="period" [ngClass]="{'am-time-badge': getDashboardTimePeriod(element.date_and_time) === 'AM', 'pm-time-badge': getDashboardTimePeriod(element.date_and_time) === 'PM'}">
														{{ getDashboardTimePeriod(element.date_and_time) }}
													</span>
												</td>
											</ng-container>

											<!-- Instructor Column -->
											<ng-container matColumnDef="instructor_name">
												<th mat-header-cell *matHeaderCellDef>Teacher</th>
												<td mat-cell *matCellDef="let element">
													<div class="instructor-cell">
														{{ element.instructor_name || 'No teacher assigned' }}
													</div>
												</td>
											</ng-container>

											<!-- Material Covered Column -->
											<ng-container matColumnDef="material_covered">
												<th mat-header-cell *matHeaderCellDef>Material Covered</th>
												<td mat-cell *matCellDef="let element">
													<div class="material-covered-cell">
														{{ element.material_covered || 'No material covered recorded' }}
													</div>
												</td>
											</ng-container>

											<!-- Comments Column -->
											<ng-container matColumnDef="comments">
												<th mat-header-cell *matHeaderCellDef>Comments</th>
												<td mat-cell *matCellDef="let element">
													<div class="comments-cell">
														{{ element.comments || 'No comments recorded' }}
													</div>
												</td>
											</ng-container>

											<!-- Lesson Rating Column -->
											<ng-container matColumnDef="lesson_rating">
												<th mat-header-cell *matHeaderCellDef>Rating</th>
												<td mat-cell *matCellDef="let element">
													<div class="rating-cell text-center">
														<ng-container *ngIf="element.lesson_rating && element.lesson_rating > 0; else noRating">
															<span class="rating-stars">
																<span *ngFor="let star of [1,2,3,4,5]"
																	  [ngClass]="{'filled-star': star <= element.lesson_rating, 'empty-star': star > element.lesson_rating}">
																	★
																</span>
															</span>
															<div class="rating-number">({{ element.lesson_rating }}/5)</div>
														</ng-container>
														<ng-template #noRating>
															<span class="text-muted">No rating</span>
														</ng-template>
													</div>
												</td>
											</ng-container>

											<tr mat-header-row *matHeaderRowDef="lessonBlockColumns"></tr>
											<tr mat-row *matRowDef="let row; columns: lessonBlockColumns;"></tr>
										</table>

										<!-- Pagination info and load more button -->
										<div class="d-flex justify-content-between align-items-center mt-3">
											<div class="text-muted">
												Showing {{ lessonBlockData.data.length }} of {{ lessonBlockTotalCount }} lessons
												<span *ngIf="lessonBlockStartFromComment" class="ms-2">
													(starting from latest comment)
												</span>
											</div>
											<button
												*ngIf="lessonBlockHasMore"
												mat-flat-button
												color="primary"
												(click)="loadMoreLessonBlocks()">
												<mat-icon>expand_more</mat-icon>
												Load More Lessons
											</button>
										</div>
									</div>

									<ng-template #noLessonBlocks>
										<div class="text-center my-5">
											<div class="alert alert-info">
												<h4>No Lesson Block Data Available</h4>
												<p class="mb-0">There are no lesson blocks with material covered, comments, or ratings recorded for this student yet.</p>
											</div>
										</div>
									</ng-template>
								</div>
							</div>
						</div>

						<div *ngIf="selectedTab === 1">
							<!-- Content for Lesson Enrollments -->
							<app-lesson-student-report
								[drupal_student_name]="data['#studentName']"
								[enrollmentsToLoad]="'DE,Bonus/Comp Lesson,Extension,Pre-Original,Original,Renewals,Transfer In,Uncategorized,History'"
								[drupal_studentAccount_id]="studentAccountID"
								[enrollmentDialogHandler]="openEnrollmentEditComponent.bind(this)"
				(dataRefreshed)="refreshDashboardData()"></app-lesson-student-report>
						</div>
						<div *ngIf="selectedTab === 2">
							<!-- Content for Payments -->
							<app-payment-reports
								[drupal_student_name]="data['#studentName']"
								[drupal_account_id]="studentAccountID" [drupal_student_id]="this.user.id"
				(dataRefreshed)="refreshDashboardData()"></app-payment-reports>

						</div>
						<div *ngIf="selectedTab === 3">
							<!-- Content for Miscellaneous -->
							<app-lesson-student-report
							[drupal_student_name]="data['#studentName']"
							[enrollmentsToLoad]="'Miscellaneous'"
							[drupal_studentAccount_id]="studentAccountID"
							[enrollmentDialogHandler]="openEnrollmentEditComponent.bind(this)"
				(dataRefreshed)="refreshDashboardData()"></app-lesson-student-report>

						</div>

						<div *ngIf="selectedTab === 4">
							<!-- Content for Sundry -->
							<app-lesson-student-report
								[drupal_student_name]="data['#studentName']"
								[enrollmentsToLoad]="'Sundry'"
								[drupal_studentAccount_id]="studentAccountID"
								[enrollmentDialogHandler]="openEnrollmentEditComponent.bind(this)"
				(dataRefreshed)="refreshDashboardData()"></app-lesson-student-report>

						</div>

						<div *ngIf="selectedTab === 5">
							<!-- Content for Non Unit Lessons -->
							<app-non-unit-lessons
							[drupal_studentAccount_id]="studentAccountID"></app-non-unit-lessons>

						</div>

						<div *ngIf="selectedTab === 6">
							<!-- Content for Non Unit Enrollments -->
							<div class="mt-3">
								<strong>Master Class Enrolled:</strong> {{ data['#totalMasterclassEnrolled'] }} •
								<strong>Master Class Taken:</strong> {{ data['#totalMasterclassTaken'] }} •
								<strong>Coaching Enrolled:</strong> {{ data['#totalCoachingEnrolled'] }} •
								<strong>Coaching Taken:</strong> {{ data['#totalCoachingTaken'] }}
							</div>
							<app-lesson-student-report
								[drupal_student_name]="data['#studentName']"
								[enrollmentsToLoad]="'Non-Unit'"
								[drupal_studentAccount_id]="studentAccountID"
								[enrollmentDialogHandler]="openEnrollmentEditComponent.bind(this)"
				(dataRefreshed)="refreshDashboardData()"></app-lesson-student-report>
						</div>

						<div *ngIf="selectedTab === 7">
							<!-- Content for Service List -->
							<div class="tab-toolbar">
								<div class="tab-toolbar__title">Service history</div>
								<div class="tab-toolbar__actions">
									<button *ngIf="_permissionsService.canAccess('lessons_create')" class="addService" mat-flat-button
										(click)="openServiceEditComponent(EventServicesEntityComponent, 'events', 'services', 'create', null, {
											'__field_students_inline_form': {
												'type': 'attendees',
												'bundle': 'attendance',
												'field_student_account': data['#studentName'] + ' (' + studentAccountID + ')'
											}
										})">
										Add Service
									</button>
								</div>
							</div>

							<!-- TABLE  -->
							<div class="row">
								<div class="col-12">
									<div class="table-responsive">
										<div class="overflow-auto">
											<table *ngIf="studentServicesRows && studentServicesRows.length > 0" mat-table [dataSource]="studentServicesRows" class="mt-4 w-100">
												<!-- Date  -->
												<ng-container matColumnDef="date">
													<th mat-header-cell *matHeaderCellDef>Date</th>
													<td mat-cell *matCellDef="let element">{{ formatDashboardDateTime(element[0].data) }}</td>
												</ng-container>

												<!-- Time  -->
												<ng-container matColumnDef="time">
													<th mat-header-cell *matHeaderCellDef>Time</th>
													<td mat-cell *matCellDef="let element">{{ element[1].data }}</td>
												</ng-container>

												<!-- Service  -->
												<ng-container matColumnDef="service">
													<th mat-header-cell *matHeaderCellDef>Service</th>
													<td mat-cell *matCellDef="let element">{{ element[2] }}</td>
												</ng-container>

												<!-- Length  -->
												<ng-container matColumnDef="length">
													<th mat-header-cell *matHeaderCellDef>Length</th>
													<td mat-cell *matCellDef="let element">{{ element[3] }}</td>
												</ng-container>

												<!-- Executive  -->
												<ng-container matColumnDef="teacher">
													<th mat-header-cell *matHeaderCellDef>Executive</th>
													<td mat-cell *matCellDef="let element">{{ element[4] }}</td>
												</ng-container>

												<!-- Status  -->
												<ng-container matColumnDef="status">
													<th mat-header-cell *matHeaderCellDef>Status</th>
													<td mat-cell *matCellDef="let element">
														<span class="badge" [ngClass]="{
															'amt-bg-gray': (element[5]?.data === 'Cancelled' || element[5]?.data === 'No Showed'),
															'amt-bg-orange': (element[5]?.data === 'Incomplete'),
															'amt-bg-red': (element[5]?.data === 'Pending Status' || element[5]?.data === 'No Sale' || element[5]?.data === 'No Status'),
															'amt-bg-purple': (element[5]?.data === 'Rescheduled'),
															'amt-bg-green': (element[5]?.data === 'Sale'),
															'amt-bg-blue': (element[5]?.data === 'Think It Over')
														}">{{ element[5]?.data }}</span>
													</td>
												</ng-container>

												<!-- Contacts Present  -->
												<ng-container matColumnDef="contacts_present">
													<th mat-header-cell *matHeaderCellDef>Contacts Present</th>
													<td mat-cell *matCellDef="let element">{{ element.contacts_present || '' }}</td>
												</ng-container>

												<!-- Edit  -->
												<ng-container matColumnDef="edit">
													<th mat-header-cell *matHeaderCellDef>Edit</th>
													<td mat-cell *matCellDef="let element">
														<mat-icon *ngIf="_permissionsService.canAccess('lessons_edit')"
															class="cursor-pointer"
															(click)="openServiceEditComponent(EventServicesEntityComponent, 'events', 'services', 'edit', element.id)">
															edit_square
														</mat-icon>
													</td>
												</ng-container>

												<tr mat-header-row *matHeaderRowDef="studentServicesColumns"></tr>
												<tr mat-row *matRowDef="let row; columns: studentServicesColumns;"></tr>
											</table>
										</div>
									</div>

									<div class="text-center my-3" *ngIf="!studentServicesRows || studentServicesRows.length === 0">
										<div class="alert alert-info">
											No services are available.
										</div>
									</div>
								</div>
							</div>

						</div>

						<div *ngIf="selectedTab === 8">
							<!-- Content for Legacy Data -->
							<div class="row">
								<div class="col-12 pt-2 pb-2">
									<div>
										<div class="tab-toolbar">
											<div class="tab-toolbar__title">Legacy account data</div>
										</div>

										<div *ngIf="!legacyData || (!legacyData.enrollments?.length && !legacyData.payments?.length && !legacyData.lessons?.length)" class="alert alert-info mb-4">
											<p class="mb-0">No legacy data available for this student.</p>
										</div>

										<!-- Legacy Enrollments Section -->
										<mat-card class="mb-4" *ngIf="legacyData?.enrollments?.length">
											<mat-card-title>Legacy Enrollments</mat-card-title>
											<mat-card-content>
												<div class="alert alert-info">
													<p class="mb-0">This section displays legacy enrollment data imported from the previous system.</p>
												</div>

												<div class="table-responsive mt-3">
													<table class="table table-striped">
														<thead>
															<tr>
																<th>Enrollment Date</th>
																<th>Program</th>
																<th>Units</th>
																<th>Payment Status</th>
																<th>Notes</th>
															</tr>
														</thead>
														<tbody>
															<tr *ngFor="let enrollment of legacyData.enrollments">
																<td>{{ formatDashboardDate(enrollment.enrollment_date) }}</td>
																<td>{{ enrollment.program }}</td>
																<td>{{ enrollment.units }}</td>
																<td>
																	{{ enrollment.payment_status }}
																</td>
																<td>{{ enrollment.notes }}</td>
															</tr>
														</tbody>
													</table>
												</div>
											</mat-card-content>
										</mat-card>

										<!-- Legacy Payments Section -->
										<mat-card class="mb-4" *ngIf="legacyData?.payments?.length">
											<mat-card-title>Legacy Payments</mat-card-title>
											<mat-card-content>
												<div class="alert alert-info">
													<p class="mb-0">This section displays legacy payment data imported from the previous system.</p>
												</div>

												<div class="table-responsive mt-3">
													<table class="table table-striped">
														<thead>
															<tr>
																<th>Payment Date</th>
																<th class="text-end">Amount</th>
																<th>Payment Method</th>
																<th>Client</th>
																<th>Status</th>
																<th>Notes</th>
															</tr>
														</thead>
														<tbody>
															<tr *ngFor="let payment of legacyData.payments">
																<td>{{ formatDashboardDate(payment.payment_date) }}</td>
																<td class="text-end">{{ payment.amount | studioCurrency }}</td>
																<td>{{ payment.payment_method }}</td>
																<td>{{ payment.program }}</td>
																<td>
																	{{ payment.status }}
																</td>
																<td>{{ payment.notes }}</td>
															</tr>
														</tbody>
													</table>
												</div>
											</mat-card-content>
										</mat-card>

										<!-- Legacy Lessons Section -->
										<mat-card *ngIf="legacyData?.lessons?.length">
											<mat-card-title>Legacy Lessons</mat-card-title>
											<mat-card-content>
												<div class="alert alert-info">
													<p class="mb-0">This section displays legacy lesson data imported from the previous system.</p>
												</div>

												<div class="table-responsive mt-3">
													<table class="table table-striped">
														<thead>
															<tr>
																<th>Date</th>
																<th>Time</th>
																<th>Teacher</th>
																<th>Type</th>
																<th>Status</th>
																<th>Notes</th>
															</tr>
														</thead>
														<tbody>
															<tr *ngFor="let lesson of legacyData.lessons">
																<td>{{ formatDashboardDate(lesson.date) }}</td>
																<td>{{ lesson.time }}</td>
																<td>{{ lesson.teacher }}</td>
																<td>{{ lesson.type }}</td>
																<td>
																	{{ lesson.status }}
																</td>
																<td>{{ lesson.notes }}</td>
															</tr>
														</tbody>
													</table>
												</div>
											</mat-card-content>
										</mat-card>
									</div>
								</div>
							</div>
						</div>

					</div>
				</div>
				</mat-card>
			</div>
		</div>
	</div>
